/**
 * Plain-English rendering of a condition tree, plus the checks that catch a
 * condition which can never do what its author meant.
 *
 * This is deliberately NOT `printConditionTree` — that one round-trips through
 * the DSL parser and must stay byte-faithful to MedDream's text form. This one
 * is throwaway prose for the summary line under the builder, so it is free to
 * use friendly field names, real operator words, and curly quotes.
 *
 * The point of the summary line: a condition assembled from dropdowns can be
 * read back and found wrong before it is saved. `main_study_modality match ""`
 * looks fine as three controls and is fatal as a sentence.
 */

import { getCombinator, getOperands } from "./conditionDsl";
import { INPUT_TYPE, getFieldLabel, getInputType, getOperatorLabel } from "./fieldRegistry";

const isRuleGroup = (node) => node && typeof node === "object" && Array.isArray(node.rules);

/** Problem codes, for the caller to translate. */
export const CONDITION_PROBLEM = {
  /** A rule with no value. See `findConditionProblems` for why each type breaks. */
  VALUE_REQUIRED: "value-required",
  /** A numeric field whose value will not parse, so the rule is dead. */
  NOT_A_NUMBER: "not-a-number",
};

const DEFAULT_WORDS = { and: "and", or: "or", not: "not", noValue: "(no value)" };

function describeRule(rule, words) {
  const inputType = rule.inputType || getInputType(rule.field);
  const field = getFieldLabel(rule.field) || rule.field || "?";
  const operator = getOperatorLabel(rule.operator, inputType);
  const raw = rule.value == null ? "" : String(rule.value);

  if (raw.trim() === "") return `${field} ${operator} ${words.noValue}`;
  if (inputType === INPUT_TYPE.NUMERIC) return `${field} ${operator} ${raw}`;
  return `${field} ${operator} “${raw}”`;
}

function describeNode(node, words, isTopLevel) {
  if (!isRuleGroup(node)) return describeRule(node, words);

  const operands = getOperands(node);
  if (operands.length === 0) return "";

  const joiner = getCombinator(node) === "or" ? words.or : words.and;
  const parts = operands.map((operand) => describeNode(operand, words, false)).filter(Boolean);
  if (parts.length === 0) return "";

  const joined = parts.join(` ${joiner} `);
  if (node.not) return `${words.not} (${joined})`;
  // A single operand carries its own grouping already; the top level never
  // needs parens because nothing sits beside it.
  if (isTopLevel || parts.length === 1) return joined;
  return `(${joined})`;
}

/**
 * One sentence describing what the tree matches, or "" when it constrains
 * nothing. Pass `words` to translate the joiners.
 */
export function describeConditionTree(group, words = {}) {
  if (!isRuleGroup(group)) return "";
  return describeNode(group, { ...DEFAULT_WORDS, ...words }, true);
}

/**
 * Rules that will not behave as their author intends.
 *
 * An empty value is the one that actually bites, and it is silent in every
 * direction: the Java evaluator drops empty elements before comparing, so
 * `match ""` is always false; `contain ""` is a substring test against the empty
 * string, so it is always true. Either way the rule is not doing what the three
 * dropdowns appear to say.
 */
export function findConditionProblems(group) {
  const problems = [];

  const walk = (node) => {
    if (!isRuleGroup(node)) {
      const inputType = node.inputType || getInputType(node.field);
      const raw = node.value == null ? "" : String(node.value);
      if (raw.trim() === "") {
        problems.push({ id: node.id, field: node.field, code: CONDITION_PROBLEM.VALUE_REQUIRED });
      } else if (inputType === INPUT_TYPE.NUMERIC && Number.isNaN(Number(raw))) {
        problems.push({ id: node.id, field: node.field, code: CONDITION_PROBLEM.NOT_A_NUMBER });
      }
      return;
    }
    getOperands(node).forEach(walk);
  };

  if (isRuleGroup(group)) walk(group);
  return problems;
}
